import { useCallback, useState } from 'react'
import type { Incident, Track } from '@/api/types'
import type { IncidentGroup } from '@/domain/grouping'
import { PipelineSm } from './Pipeline'
import { CardSkeleton, PriorityPill, RefreshButton } from './atoms'
import { Icon } from './Icons'
import { useResizableStrip } from '@/hooks/useResizableStrip'

interface Props {
  groups: IncidentGroup[]
  truncated: boolean
  track: Track
  selectedId: string | null
  onSelect: (incident: Incident) => void
  loading: boolean
  refreshing: boolean
  onRefresh: () => void
}

/**
 * Sección 1 · Listado.
 *
 * Una sola carga de hasta 100 incidencias, sin scroll infinito. Se agrupan por
 * `status2` en barras colapsables —todas cerradas al abrir, con un botón en la
 * cabecera para expandirlas o contraerlas juntas— y dentro de cada grupo van
 * por prioridad y fecha (domain/grouping.ts). En móvil cada grupo abierto es
 * una fila horizontal; desde 1024 px, una columna en el riel.
 *
 * El número de cada barra cuenta lo cargado, no el total: pedirlo obliga a
 * `count=true`, que recorre 183.780 documentos y cuesta 560 ms por consulta
 * sobre una base compartida (corrección §2.2).
 */
export function IncidentList({
  groups,
  truncated,
  track,
  selectedId,
  onSelect,
  loading,
  refreshing,
  onRefresh,
}: Props) {
  const strip = useResizableStrip()

  // Por clave de grupo, que es el id de `status2`: estable entre recargas, así
  // que "Actualizar" no vuelve a contraer lo que el usuario abrió. Un grupo
  // que aparece nuevo nace contraído, igual que todos al abrir.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())

  const allOpen = groups.length > 0 && groups.every((g) => expanded.has(g.key))
  const count = groups.reduce((n, g) => n + g.incidents.length, 0)

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setExpanded(allOpen ? new Set() : new Set(groups.map((g) => g.key)))
  }, [allOpen, groups])

  const toggleAllLabel = allOpen ? 'Contraer todos los grupos' : 'Expandir todos los grupos'

  return (
    <section className="sec sec-list" aria-labelledby="h-list">
      <div className="sec-hd">
        <h2 id="h-list">Incidencias</h2>
        <button
          type="button"
          className="hd-btn"
          onClick={toggleAll}
          disabled={groups.length === 0}
          aria-label={toggleAllLabel}
          title={toggleAllLabel}
        >
          <Icon name={allOpen ? 'collapse' : 'expand'} size={15} />
        </button>
        <RefreshButton
          onClick={onRefresh}
          busy={refreshing}
          label="Actualizar el listado de incidencias"
        />
      </div>

      <div
        className="list-scroll"
        id="list-scroll"
        ref={strip.scrollRef}
        data-mode={strip.mode}
        style={strip.style}
      >
        {/* Un listado cortado no puede verse igual que uno completo. */}
        {truncated && (
          <p className="list-note">
            Se muestran las {count} incidencias más recientes. Puede haber otras más
            antiguas que no aparecen acá.
          </p>
        )}

        <div className="groups" aria-label="Listado de incidencias">
          {groups.map((group) => {
            const open = expanded.has(group.key)
            const bodyId = `grp-${group.key}`
            const hasSelected = group.incidents.some((i) => i._id === selectedId)
            return (
              <div className="group" key={group.key}>
                <h3 className="group-hd">
                  <button
                    type="button"
                    className="group-bar"
                    aria-expanded={open}
                    aria-controls={bodyId}
                    data-has-selected={hasSelected || undefined}
                    onClick={() => toggle(group.key)}
                  >
                    <span className="group-chev">
                      <Icon name="chevron" size={14} />
                    </span>
                    <span className="group-label">{group.label}</span>
                    <span className="group-count">{group.incidents.length}</span>
                  </button>
                </h3>

                <div className="group-body" id={bodyId} role="group" aria-label={group.label} hidden={!open}>
                  {group.incidents.map((incident) => (
                    <button
                      key={incident._id}
                      type="button"
                      className="card-mini"
                      aria-current={selectedId === incident._id}
                      onClick={() => onSelect(incident)}
                    >
                      <div className="cm-top">
                        <span className="cm-serial">{incident.ticket}</span>
                        <span style={{ marginLeft: 'auto' }}>
                          <PriorityPill priority={incident.priority} />
                        </span>
                      </div>
                      <p className="cm-name">{incident.name}</p>
                      <div className="cm-foot">
                        <PipelineSm state={incident.state} track={track} />
                        <span className="cm-ots">
                          {incident.otCount} OT{incident.otCount === 1 ? '' : 's'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Arrastrar hacia abajo muestra más tarjetas sin scrollear: cada grupo
          abierto pasa de fila a grilla. */}
      <div
        className="resizer"
        role="separator"
        aria-orientation="horizontal"
        tabIndex={0}
        aria-label="Arrastra para mostrar más incidencias"
        title="Arrastra para mostrar más incidencias"
        {...strip.handleProps}
      />

      {loading && groups.length === 0 && (
        <div className="sec-in" style={{ paddingTop: 4 }}>
          <CardSkeleton />
        </div>
      )}
    </section>
  )
}
